import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

interface MinecraftCharacterProps {
    textureUrl: string; // 외부에서 불러올 텍스처 URL
}

const MinecraftCharacter: React.FC<MinecraftCharacterProps> = ({ textureUrl }) => {
    const meshRef = useRef<THREE.Group>(null);
    const mixerRef = useRef<THREE.AnimationMixer | null>(null);
    const requestRef = useRef<number | null>(null);
    const [modelUrl, setModelUrl] = useState<string>(''); // 모델 파일 URL 상태
    const modelUrl32 = `${process.env.PUBLIC_URL}/models/player32.glb`; // 64x32 텍스처용 모델 파일 경로

    useEffect(() => {
        const loader = new GLTFLoader();
        const textureLoader = new THREE.TextureLoader();

        // 텍스처를 로드하고 크기를 확인하는 함수
        const loadTextureAndModel = async () => {
            try {
                // 외부 텍스처를 불러옴
                const texture = await new Promise<THREE.Texture>((resolve, reject) => {
                    textureLoader.load(
                        textureUrl,
                        (loadedTexture) => {
                            // 텍스처 속성 설정
                            loadedTexture.flipY = false;
                            loadedTexture.wrapS = THREE.ClampToEdgeWrapping;
                            loadedTexture.wrapT = THREE.ClampToEdgeWrapping;
                            loadedTexture.minFilter = THREE.NearestFilter;
                            loadedTexture.magFilter = THREE.NearestFilter;

                            resolve(loadedTexture);
                        },
                        undefined,
                        (error) => reject(error)
                    );
                });

                // 텍스처 크기 확인
                const isExpandedTexture = texture.image.width === 64 && texture.image.height === 64;

                // 텍스처 크기에 따라 모델 URL 선택
                const selectedModelUrl = isExpandedTexture ? `${process.env.PUBLIC_URL}/models/player.glb` : modelUrl32;
                setModelUrl(selectedModelUrl);

                // 알파 맵을 위한 텍스처를 로드
                const alphaTexture = await new Promise<THREE.Texture>((resolve, reject) => {
                    textureLoader.load(
                        textureUrl, // 같은 URL로 알파맵을 사용한다고 가정
                        (loadedTexture) => {
                            // 알파 맵 텍스처 속성 설정
                            loadedTexture.flipY = false;
                            loadedTexture.wrapS = THREE.ClampToEdgeWrapping;
                            loadedTexture.wrapT = THREE.ClampToEdgeWrapping;
                            loadedTexture.minFilter = THREE.NearestFilter;
                            loadedTexture.magFilter = THREE.NearestFilter;

                            resolve(loadedTexture);
                        },
                        undefined,
                        (error) => reject(error)
                    );
                });

                // 선택된 모델 URL로 모델을 불러옴
                const gltf = await loader.loadAsync(selectedModelUrl);

                const avatarModel = gltf.scene;
                avatarModel.scale.set(2.5, 2.5, 2.5);
                avatarModel.position.set(0, -3.2, 0);
                avatarModel.rotation.set(0, 60, 0);

                // 텍스처를 모델에 적용
                avatarModel.traverse((child: any) => {
                    if (child.isMesh) {
                        const material = new THREE.MeshStandardMaterial({
                            map: texture, // 기본 텍스처
                            transparent: true, // 투명도 적용
                            alphaTest: 0.5, // 알파 값이 0.5 이하인 경우 투명 처리
                            depthWrite: true // 깊이 버퍼에 쓰기 비활성화
                        });
                        child.material = material;
                    }
                });

                if (meshRef.current) {
                    meshRef.current.add(avatarModel);
                }

                console.log('모델이 성공적으로 로드되었습니다:', avatarModel);

                // 애니메이션 적용
                const mixer = new THREE.AnimationMixer(avatarModel);
                mixerRef.current = mixer;

                const idleAnimation = gltf.animations.find((clip: THREE.AnimationClip) => clip.name === 'Idle_default');
                if (idleAnimation) {
                    const action = mixer.clipAction(idleAnimation);
                    action.play();
                }

                // 애니메이션 루프 설정
                const clock = new THREE.Clock();
                const animate = () => {
                    if (mixerRef.current) {
                        mixerRef.current.update(clock.getDelta());
                    }
                    requestRef.current = requestAnimationFrame(animate);
                };
                animate();
            } catch (error) {
                console.error('텍스처 또는 모델 로딩에 실패했습니다:', error);
            }
        };

        loadTextureAndModel();

        return () => {
            if (requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
            if (mixerRef.current) {
                mixerRef.current.stopAllAction();
            }
            if (meshRef.current) {
                meshRef.current.remove(meshRef.current.children[0]);
            }
        };
    }, [textureUrl]);

    return <group ref={meshRef} />;
};

export default MinecraftCharacter;
